import {
  isTerminalSessionError,
  receiverChallenge,
  receiverSecret,
} from "@browsemate/auth-client";
import {
  browserSessionClient,
  extensionRequest,
  withBrowserSessionLock,
  type BrowserSession,
} from "./browser-session";
import {
  deliverToExtension,
  forgetLegacyWebSession,
  webSession,
} from "./legacy-account";
import { invalidateHostedLogins } from "./hosted-login";

const browser = browserSessionClient();
type Guard = () => boolean;
function assertCurrent(current: Guard) {
  if (!current()) throw new Error("STALE_LOGIN");
}
export async function restoreBrowser(
  current: Guard,
  signal: AbortSignal,
): Promise<{
  session?: BrowserSession;
  extensionReady: boolean;
  freshReceiver?: string;
  receiverChallenge?: string;
}> {
  let session = await withBrowserSessionLock(async () => {
    assertCurrent(current);
    let value = await browser.status();
    assertCurrent(current);
    if (sessionStorage.getItem("browsemate.session")) {
      // The old tab's refresh credential is used once to bind the new server
      // cookie, then removed. Never copy it to another tab or the extension.
      const restored = await webSession()
        .restore()
        .catch((error: unknown) => {
          if (isTerminalSessionError(error)) return undefined;
          throw error;
        });
      assertCurrent(current);
      if (restored) {
        const linked = await webSession().authorized((token) =>
          browser.link(token),
        );
        assertCurrent(current);
        value = linked;
        forgetLegacyWebSession();
      }
    }
    return value;
  });
  const extension = await extensionRequest(
    "BROWSEMATE_BROWSER_PREPARE_V1",
    {},
    signal,
  );
  assertCurrent(current);
  if (extension?.ok && extension.alreadyAuthenticated) {
    const verifier = receiverSecret();
    const grant = await extensionRequest(
      "BROWSEMATE_BROWSER_GRANT_V1",
      { receiverChallenge: await receiverChallenge(verifier) },
      signal,
    );
    assertCurrent(current);
    if (grant?.code === "AUTH_REQUIRED")
      return { ...(session ? { session } : {}), extensionReady: false };
    if (!grant?.ok || !grant.exchangeCode || !grant.accountId)
      throw new Error("EXTENSION_SESSION_UNAVAILABLE");
    const exchangeCode = grant.exchangeCode;
    session = await withBrowserSessionLock(async () => {
      assertCurrent(current);
      const existing = await browser.status();
      assertCurrent(current);
      if (existing && existing.account_id !== grant.accountId)
        throw new Error("ACCOUNT_MISMATCH");
      const value = await browser.exchange(exchangeCode, verifier);
      assertCurrent(current);
      if (value.account_id !== grant.accountId)
        throw new Error("ACCOUNT_MISMATCH");
      return value;
    });
    return { session, extensionReady: true };
  }
  return {
    ...(session ? { session } : {}),
    extensionReady: false,
    ...(extension?.ok && extension.receiverChallenge
      ? { receiverChallenge: extension.receiverChallenge }
      : {}),
    ...(extension?.ok &&
    extension.freshAuthenticationRequired &&
    extension.receiverChallenge
      ? { freshReceiver: extension.receiverChallenge }
      : {}),
  };
}

export async function exchangeBrowser(
  code: string,
  verifier: string,
  current: Guard,
) {
  return withBrowserSessionLock(async () => {
    assertCurrent(current);
    const session = await browser.exchange(code, verifier);
    assertCurrent(current);
    return session;
  });
}

export async function connectExtension(
  session: BrowserSession,
  challenge: string | undefined,
  current: Guard,
  signal: AbortSignal,
): Promise<boolean> {
  const extension = await extensionRequest(
    "BROWSEMATE_BROWSER_PREPARE_V1",
    {},
    signal,
  );
  assertCurrent(current);
  if (extension?.alreadyAuthenticated) {
    const verifier = receiverSecret();
    const grant = await extensionRequest(
      "BROWSEMATE_BROWSER_GRANT_V1",
      { receiverChallenge: await receiverChallenge(verifier) },
      signal,
    );
    assertCurrent(current);
    if (!grant?.ok || !grant.accountId || !grant.exchangeCode)
      throw new Error("EXTENSION_SESSION_UNAVAILABLE");
    if (grant.accountId !== session.account_id)
      throw new Error("ACCOUNT_MISMATCH");
    const linked = await exchangeBrowser(grant.exchangeCode, verifier, current);
    if (linked.account_id !== session.account_id)
      throw new Error("ACCOUNT_MISMATCH");
    return true;
  }
  if (extension?.freshAuthenticationRequired) return false;
  const receiver = challenge ?? extension?.receiverChallenge;
  if (!receiver) return false;
  const grant = await withBrowserSessionLock(async () => {
    assertCurrent(current);
    const result = await browser.handoff(receiver);
    assertCurrent(current);
    if (result.account_id !== session.account_id)
      throw new Error("ACCOUNT_MISMATCH");
    return result;
  });
  assertCurrent(current);
  const ok = challenge
    ? await deliverToExtension(grant.exchange_code, true, challenge)
    : (
        await extensionRequest(
          "BROWSEMATE_BROWSER_EXCHANGE_V1",
          { receiverChallenge: receiver, exchangeCode: grant.exchange_code },
          signal,
        )
      )?.ok === true;
  assertCurrent(current);
  if (!ok) throw new Error("HANDOFF_FAILED");
  return true;
}

export async function logoutBrowser() {
  invalidateHostedLogins();
  const cancelled = await extensionRequest(
    "BROWSEMATE_BROWSER_CANCEL_LOGIN_V1",
  );
  if (!cancelled) {
    // Older bridges understand PREPARE but cannot cancel a pending exchange.
    // Silence from CANCEL alone is not evidence that no extension is installed.
    const existing = await extensionRequest("BROWSEMATE_BROWSER_PREPARE_V1");
    if (existing) throw new Error("EXTENSION_UPDATE_REQUIRED");
  }
  if (cancelled && !cancelled.ok) throw new Error("EXTENSION_CANCEL_FAILED");
  await withBrowserSessionLock(async () => {
    await browser.logout();
    forgetLegacyWebSession();
  });
}
